import { api, type RouterOutputs } from "~/utils/api"
import { runPartialRefresh, type PartialRefreshOps } from "~/utils/cacheUtils/runPartialRefresh"

export type IStockOutTimesV2 = RouterOutputs["productTableData"]["stockOutTimesV2"]

const emptyStockOutTimes = (): IStockOutTimesV2 => ({
  stockOutTimes: new Map(),
  leadTimeAvailability: new Map(),
})

/**
 * Merges new stockOutTimes entries (keyed per skuId) into the existing payload.
 * Updated entries override existing ones; unrelated SKUs are preserved.
 * Always returns new Maps for React Query / AG-Grid identity.
 *
 * Both halves are merged: they come off different walks for the same SKUs, so refreshing one and
 * not the other leaves a row's stockout dates and its lead-time residual describing different data.
 */
export function mergeStockOutTimesIntoCache(
  existingData: IStockOutTimesV2 | undefined,
  updatedData: IStockOutTimesV2 | undefined,
): IStockOutTimesV2 {
  if (!existingData) return updatedData ?? emptyStockOutTimes()
  if (!updatedData || updatedData.stockOutTimes.size === 0) return existingData

  const stockOutTimes = new Map(existingData.stockOutTimes)
  const leadTimeAvailability = new Map(existingData.leadTimeAvailability)
  for (const [skuId, info] of updatedData.stockOutTimes) {
    stockOutTimes.set(skuId, info)
    // A refreshed SKU that no longer carries a residual (lead time cleared, forecast horizon
    // passed) must lose its old one, not keep it.
    const byWarehouse = updatedData.leadTimeAvailability.get(skuId)
    if (byWarehouse) leadTimeAvailability.set(skuId, byWarehouse)
    else leadTimeAvailability.delete(skuId)
  }
  return { stockOutTimes, leadTimeAvailability }
}

/**
 * Refreshes the stockOutTimes cache for the SKUs of the affected variants.
 * Uses the same `stockOutTimesV2` endpoint with `{ variantIds }`; the server
 * resolves variantIds → skuIds and returns just that slice for merging.
 */
export function stockOutTimesRefreshOps(
  utils: ReturnType<typeof api.useUtils>,
  variantIds: string[],
): PartialRefreshOps<IStockOutTimesV2> {
  return {
    cancel: () => {
      // Only a refetch can land on top of the merge; a cancelled first load is never restarted.
      if (utils.productTableData.stockOutTimesV2.getData({})) {
        void utils.productTableData.stockOutTimesV2.cancel()
      }
    },
    // Outside the query cache, so a concurrent refresh's cancel() cannot abort this slice.
    fetch: () => utils.client.productTableData.stockOutTimesV2.query({ variantIds }),
    apply: (updated) => {
      if (updated.stockOutTimes.size === 0) return
      utils.productTableData.stockOutTimesV2.setData({}, (oldData) => {
        if (!oldData) return oldData
        const merged = mergeStockOutTimesIntoCache(oldData, updated)
        if (!merged || merged.stockOutTimes.size === 0) {
          console.warn("StockOutTimes cache merge returned empty data, keeping old data")
          return oldData
        }
        return merged
      })
    },
    fallback: () => {
      void utils.productTableData.stockOutTimesV2.invalidate()
    },
  }
}

export async function refreshStockOutTimesCache(
  utils: ReturnType<typeof api.useUtils>,
  variantIds: string[],
) {
  if (!variantIds || variantIds.length === 0) return
  await runPartialRefresh(stockOutTimesRefreshOps(utils, variantIds))
}

export function usePartialRefreshStockOutTimes() {
  const utils = api.useUtils()
  return async (variantIds: string[]) => {
    await refreshStockOutTimesCache(utils, variantIds)
  }
}
